/*
 * The chassis is an L: a masthead across the top and a nav rail down the
 * left, both on the raised surface, with the work happening in the well they
 * enclose. The brand lives on the masthead because the rail is too narrow to
 * render the lockup legibly, and because the masthead is where the promo
 * slot has to sit.
 */
.shell {
  display: grid;
  grid-template-columns: var(--sidebar-width) minmax(0, 1fr);
  grid-template-rows: auto minmax(0, 1fr);
  min-height: 100dvh;
  width: 100%;
}

/* The mesh is the chassis material: a screen is a woven grid, so the parts
   that stand for equipment carry one and the work surface does not. */
.masthead,
.sidebar {
  background-color: var(--surface-raised);
  background-image:
    repeating-linear-gradient(
      90deg,
      var(--mesh-line) 0 1px,
      transparent 1px var(--mesh-pitch)
    ),
    repeating-linear-gradient(
      0deg,
      var(--mesh-line) 0 1px,
      transparent 1px var(--mesh-pitch)
    );
}

.masthead {
  grid-column: 1 / -1;
  grid-row: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-8);
  padding: var(--space-4) var(--space-8) var(--space-4) var(--space-3);
  border-bottom: 1px solid var(--line-subtle);
}

.brand {
  display: block;
  min-width: 0;
}

/* The artwork is drawn white-on-transparent for the dark ground it sits on;
   it needs no plate of its own. Sized by HEIGHT, not width, so the lockup
   keeps a fixed optical weight no matter how wide the masthead runs. At
   2.75rem the tagline clears legibility; below about 2.25rem it does not. */
.brandLockup {
  display: block;
  height: 2.75rem;
  width: auto;
}

/*
 * Sponsored inventory. It shrinks but never grows past a 728px leaderboard,
 * and it is pinned to the right so the gap between the brand and the unit
 * absorbs every width in between.
 *
 * The floor lives HERE rather than on the masthead, and that placement is
 * the whole point: the element exists as soon as the shell knows it has
 * inventory to show, so the 90px is reserved before the creative loads and
 * nothing below it moves when it arrives. A shell with no inventory pays
 * nothing -- the masthead falls back to the height of the lockup instead of
 * holding open a band of empty chrome.
 */
.promo {
  flex: 0 1 var(--promo-max);
  display: flex;
  justify-content: flex-end;
  align-items: center;
  min-width: 0;
  min-height: var(--masthead-min);
}

.sidebar {
  grid-column: 1;
  grid-row: 2;
  border-right: 1px solid var(--line-subtle);
  padding: var(--space-5) var(--sidebar-gutter) var(--space-5);
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

.desktopNavigation {
  display: flex;
  flex: 1;
}

.navGroups {
  display: flex;
  flex: 1;
  flex-direction: column;
}

.navGroup {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.navGroup + .navGroup {
  margin-top: var(--space-4);
  padding-top: var(--space-4);
  border-top: 1px solid var(--line-subtle);
}

.navGroup[data-nav-group='account'] {
  margin-top: auto;
}

/* Home is the rail's lead item and heads no group of its own. */
.navGroup:first-child .navGroupLabel {
  display: none;
}

/* Captions clear the marker gutter so their first glyph lands on the same
   vertical as the section labels below them and the lockup above. */
.navGroupLabel {
  padding-left: calc(var(--sidebar-marker) + var(--space-3));
  padding-right: var(--space-3);
  font-family: var(--font-display);
  font-size: var(--text-2xs);
  letter-spacing: var(--tracking-caps);
  text-transform: uppercase;
  color: var(--ink-secondary);
}

.navList {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.navLink {
  display: block;
  width: 100%;
  text-align: left;
  padding: var(--space-2) var(--space-3);
  border: none;
  /* The marker rides in this gutter, so the label never shifts sideways when
     the current section changes. */
  border-left: var(--sidebar-marker) solid transparent;
  border-radius: 0;
  background: none;
  color: var(--ink-secondary);
  font-family: var(--font-display);
  font-size: var(--text-sm);
  min-height: 2.75rem;
  letter-spacing: var(--tracking-wide);
  cursor: pointer;
  transition: background var(--duration-fast) var(--ease),
    color var(--duration-fast) var(--ease),
    border-color var(--duration-fast) var(--ease);
}

.navLink:hover {
  background: var(--surface-hover);
  color: var(--ink-primary);
  border-left-color: var(--line-strong);
}

/* Current section is marked by a solid marker and text weight, not colour
   alone, so it survives greyscale and high-contrast modes (spec 13). */
.navLink[aria-current='page'] {
  background: var(--accent-wash);
  border-left-color: var(--accent);
  color: var(--accent-strong);
  font-weight: 600;
}

.signOut {
  color: var(--ink-muted);
}

.compactNavigation {
  display: none;
}

/*
 * The foot of the rail. Pushed to the bottom edge so it lines up with the
 * status bar in the next column: together they close the chassis with one
 * continuous band of state rather than leaving 250px of empty chrome.
 */
.railStatus {
  margin-top: auto;
  padding: var(--space-3) 0 0 calc(var(--sidebar-marker) + var(--space-3));
  border-top: 1px solid var(--line-subtle);
}

.main {
  grid-column: 2;
  grid-row: 2;
  /* A column, so the status bar can be pushed to the bottom edge. Without it
     content trails off into open space on any short screen. */
  display: flex;
  flex-direction: column;
  padding: var(--space-8) var(--space-8) 0;
  min-width: 0;
}

.content {
  flex: 1;
  min-width: 0;
  padding-bottom: var(--space-12);
}

/*
 * The status bar is the bottom edge of the chassis. It states where the data
 * physically is, which is the one thing a local-first tool has to keep in
 * front of the person using it.
 */
.status {
  margin-top: auto;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space-4);
  flex-wrap: wrap;
  padding: var(--space-3) 0;
  border-top: 1px solid var(--line-subtle);
  font-family: var(--font-display);
  font-size: var(--text-2xs);
  letter-spacing: var(--tracking-caps);
  text-transform: uppercase;
  color: var(--ink-muted);
}

.statusDot {
  color: var(--accent-dim);
}

@media (max-width: 860px) {
  /* Three auto rows inside a 100dvh grid all stretch under the default
     `align-content: normal`, which handed the nav half the phone screen.
     Pinning masthead and nav to their content keeps the fold for content. */
  .shell {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto auto minmax(0, 1fr);
  }

  .masthead {
    grid-column: 1;
    /* The unit wraps under the brand rather than squeezing it. */
    flex-wrap: wrap;
    gap: var(--space-3);
    padding: var(--space-4);
  }

  .brandLockup {
    height: 2.25rem;
  }

  /* A 728px leaderboard cannot run on a phone anyway, so the slot drops its
     desktop floor and takes whatever the mobile creative needs. */
  .promo {
    flex-basis: 100%;
    justify-content: flex-start;
    min-height: 0;
  }

  .sidebar {
    grid-column: 1;
    grid-row: 2;
    border-right: none;
    border-bottom: 1px solid var(--line-subtle);
    padding: 0;
    gap: 0;
  }

  .desktopNavigation {
    display: none;
  }

  .compactNavigation {
    display: block;
  }

  .compactNavList {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    list-style: none;
  }

  .compactNavList > li + li {
    border-left: 1px solid var(--line-subtle);
  }

  .compactNavLink {
    display: block;
    width: 100%;
    min-height: 3rem;
    padding: var(--space-3) var(--space-1)
      calc(var(--space-3) - var(--sidebar-marker));
    border: none;
    border-bottom: var(--sidebar-marker) solid transparent;
    border-radius: 0;
    background: transparent;
    color: var(--ink-secondary);
    font-family: var(--font-display);
    font-size: var(--text-xs);
    letter-spacing: var(--tracking-wide);
    cursor: pointer;
    transition: background var(--duration-fast) var(--ease),
      color var(--duration-fast) var(--ease),
      border-color var(--duration-fast) var(--ease);
  }

  .compactNavLink:hover,
  .compactNavLink[aria-expanded='true'] {
    background: var(--surface-hover);
    color: var(--ink-primary);
  }

  .compactNavLink[aria-current='page'] {
    border-bottom-color: var(--accent);
    color: var(--accent-strong);
    font-weight: 600;
  }

  .compactMorePanel {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(9rem, 1fr));
    gap: var(--space-4);
    padding: var(--space-4);
    border-top: 1px solid var(--line-strong);
    background: var(--surface-raised);
  }

  .compactMoreGroup {
    min-width: 0;
    padding-top: var(--space-3);
    border-top: 1px solid var(--line-subtle);
  }

  .compactMoreLabel {
    display: block;
    padding: 0 var(--space-3) var(--space-2);
    color: var(--ink-secondary);
    font-family: var(--font-display);
    font-size: var(--text-2xs);
    letter-spacing: var(--tracking-caps);
    text-transform: uppercase;
  }

  .compactMoreList {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    list-style: none;
  }

  .compactMoreLink {
    display: block;
    width: 100%;
    min-height: 2.75rem;
    padding: var(--space-2) var(--space-3);
    border: none;
    border-left: var(--sidebar-marker) solid transparent;
    border-radius: 0;
    background: transparent;
    color: var(--ink-secondary);
    font-family: var(--font-display);
    font-size: var(--text-sm);
    letter-spacing: var(--tracking-wide);
    text-align: left;
    cursor: pointer;
  }

  .compactMoreLink:hover {
    background: var(--surface-hover);
    color: var(--ink-primary);
  }

  .compactMoreLink[aria-current='page'] {
    background: var(--accent-wash);
    border-left-color: var(--accent);
    color: var(--accent-strong);
    font-weight: 600;
  }

  /* The rail readout is a desktop affordance; the phone already carries the
     same state in the status bar at the foot of the content column. */
  .railStatus {
    display: none;
  }

  .main {
    grid-column: 1;
    grid-row: 3;
    padding: var(--space-5) var(--space-4) 0;
  }

  .content {
    padding-bottom: var(--space-8);
  }
}
